import React, { createContext, useContext, useState, useEffect } from 'react';
import { getConfig } from '../config/environment';
import { hasAuthorizedContentAdminToken } from '../utils/adminToken';

const JackpotStyleContext = createContext();

const DEFAULT_JACKPOT_STYLE = 'auto';

const isAdminSession = () => hasAuthorizedContentAdminToken(getConfig().tlfId);
const storedJackpotStyle = () =>
  localStorage.getItem('jackpotStyle') || DEFAULT_JACKPOT_STYLE;

export function JackpotStyleProvider({ children }) {
  const [jackpotStyle, setJackpotStyle] = useState(() => storedJackpotStyle());
  const [loading] = useState(false);

  // External updates (e.g. dropdown in another tree branch) broadcast via event bus.
  useEffect(() => {
    const handleStyleChange = (event) => {
      const next = event?.detail;
      if (typeof next === 'string' && next) {
        setJackpotStyle(next);
      }
    };
    window.addEventListener('jackpotStyleChange', handleStyleChange);
    return () => window.removeEventListener('jackpotStyleChange', handleStyleChange);
  }, []);

  // Publish/discard resets the local override back to the default.
  useEffect(() => {
    const handleReset = () => {
      try { localStorage.removeItem('jackpotStyle'); } catch {}
      setJackpotStyle(DEFAULT_JACKPOT_STYLE);
    };
    window.addEventListener('appearanceReset', handleReset);
    return () => window.removeEventListener('appearanceReset', handleReset);
  }, []);

  const updateJackpotStyle = (newStyle) => {
    setJackpotStyle(newStyle);
    if (isAdminSession()) {
      try { localStorage.setItem('jackpotStyle', newStyle); } catch {}
    }
    window.dispatchEvent(new CustomEvent('jackpotStyleChange', { detail: newStyle }));
  };

  return (
    <JackpotStyleContext.Provider
      value={{
        jackpotStyle,
        updateJackpotStyle,
        loading,
      }}
    >
      {children}
    </JackpotStyleContext.Provider>
  );
}

export function useJackpotStyle() {
  const context = useContext(JackpotStyleContext);
  if (!context) {
    throw new Error('useJackpotStyle must be used within a JackpotStyleProvider');
  }
  return context;
}
